import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.date.to-string.js";
import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect } from 'react';
import Head from 'next/head';
import getConfig from 'next/config';
import notification from "antd/lib/notification";
import '@store/utils/lib/styles';
import { appWithTranslation } from '@meepshop/locales';
import ActionButton from '@meepshop/action-button';
import { StoreDesignProvider } from '@meepshop/context/lib/StoreDesign';
import { AppsProvider } from '@meepshop/context/lib/Apps';
import { RoleProvider } from '@meepshop/context/lib/Role';
import { SensorProvider } from '@meepshop/context/lib/Sensor';
import { FormDataProvider } from '@meepshop/form-data';
import { GlobalProvider } from '@meepshop/context/lib/Global';
import FbProvider from '@store/fb';
import CurrencyProvider from '@store/currency';
import AdTrackProvider from '@store/ad-track';
import cookie from 'js-cookie';
import { log } from '@meepshop/logger/lib/gqls/log';
import isPageInvisible from '@meepshop/utils/lib/isPageInvisible';
import { CloseView, ErrorPageNotFound, ErrorBoundary, InvisibleView } from "../components";
import { appWithApollo } from "../utils/withApollo";
import usePage from "../hooks/usePage";
import useExpiringPoints from "../hooks/useExpiringPoints";
import useInitialCart from "../hooks/useInitialCart";
import useGetGlobalCarousel from "../hooks/useGetGlobalCarousel";
import useGetGlobalPopup from "../hooks/useGetGlobalPopup";

var _getConfig = getConfig(),
    ENV = _getConfig.publicRuntimeConfig.ENV;

var env = ENV || 'stage';
var isProduction = env === 'production';
notification.config({
  placement: 'topRight',
  duration: 1.5
});

var App = function App(props) {
  var _faviconImage$scaledS, _faviconImage$scaledS2, _lcpImage$scaledSrc, _lcpImage$scaledSrc2, _lcpImage$scaledSrc3, _lcpImage$scaledSrc4, _lcpImage$scaledSrc5, _lcpImage$scaledSrc6, _lcpImage$scaledSrc7, _logoImage$scaledSrc;

  var closed = props.closed,
      Component = props.Component,
      pageProps = props.pageProps;

  var _usePage = usePage(),
      i18n = _usePage.i18n,
      router = _usePage.router,
      client = _usePage.client,
      storeName = _usePage.storeName,
      storeDescription = _usePage.storeDescription,
      logoImage = _usePage.logoImage,
      faviconImage = _usePage.faviconImage,
      lcpImage = _usePage.lcpImage,
      backToTopButtonEnabled = _usePage.backToTopButtonEnabled,
      product = _usePage.product,
      loading = _usePage.loading,
      page = _usePage.page;
  /* 全域元件：全站廣告 */


  var _useGetGlobalCarousel = useGetGlobalCarousel(),
      globalCarousel = _useGetGlobalCarousel.globalCarousel;
  /* 全域元件：彈窗廣告 */


  var _useGetGlobalPopup = useGetGlobalPopup(),
      globalPopup = _useGetGlobalPopup.globalPopup;

  useExpiringPoints();
  useInitialCart();
  useEffect(function () {
    var cookieBrowserLocale = cookie.get('next-i18next-browser'); // 因為有 cache 之後，不會經過 next server 進行判斷，所以 initialLanguage 都會是 zh_TW，只要 refresh 頁面 browser cookie 都會被轉為 zh_TW，故寫一個 browser 的來 overwrite 它

    if (cookieBrowserLocale && i18n.language !== cookieBrowserLocale) {
      i18n.changeLanguage(cookieBrowserLocale);
      cookie.set('next-i18next-browser', cookieBrowserLocale);
    }

    window.history.scrollRestoration = 'manual';
    window.addEventListener('unhandledrejection', function (event) {
      client.mutate({
        mutation: log,
        variables: {
          input: {
            type: 'ERROR',
            name: 'UNHANDLED_REJECTION',
            data: {
              message: event.reason.message,
              stack: event.reason.stack
            }
          }
        }
      });
    });
  }, [client, i18n, router.locale]);
  /* 頁面上下架狀態（只有分頁有這個功能 pageType: custom，其餘 isVisible 都會回傳 true）see: https://app.clickup.com/t/3770197/TK-7018 */

  if (!loading && isPageInvisible({
    now: new Date(),
    isVisible: page === null || page === void 0 ? void 0 : page.isVisible,
    startTime: page === null || page === void 0 ? void 0 : page.visibleStartTime,
    endTime: page === null || page === void 0 ? void 0 : page.visibleEndTime
  })) return /*#__PURE__*/React.createElement(InvisibleView, null);
  /* Store is closed */

  if (closed) return /*#__PURE__*/React.createElement(CloseView, {
    closed: closed
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Head, null, /*#__PURE__*/React.createElement("meta", {
    httpEquiv: "x-ua-compatible",
    content: "ie=edge"
  }), /*#__PURE__*/React.createElement("meta", {
    name: "viewport",
    content: "width=device-width, initial-scale=1, maximum-scale=1"
  }), /*#__PURE__*/React.createElement("meta", {
    name: "format-detection",
    content: "telephone=no"
  }), /*#__PURE__*/React.createElement("title", null, storeName), /*#__PURE__*/React.createElement("link", {
    rel: "icon",
    type: "image/png",
    href: faviconImage === null || faviconImage === void 0 ? void 0 : (_faviconImage$scaledS = faviconImage.scaledSrc) === null || _faviconImage$scaledS === void 0 ? void 0 : _faviconImage$scaledS.w60
  }), /*#__PURE__*/React.createElement("link", {
    rel: "apple-touch-icon",
    href: faviconImage === null || faviconImage === void 0 ? void 0 : (_faviconImage$scaledS2 = faviconImage.scaledSrc) === null || _faviconImage$scaledS2 === void 0 ? void 0 : _faviconImage$scaledS2.w60
  }), /*#__PURE__*/React.createElement("link", {
    rel: "dns-prefetch",
    href: isProduction ? 'https://img.meepshop.com' : 'https://img.meepstage.com'
  }), /*#__PURE__*/React.createElement("link", {
    rel: "preconnect",
    href: isProduction ? 'https://img.meepshop.com' : 'https://img.meepstage.com'
  }), /*#__PURE__*/React.createElement("link", {
    rel: "dns-prefetch",
    href: isProduction ? 'https://cdn.meepshop.com' : 'https://cdn.meepstage.com/'
  }), /*#__PURE__*/React.createElement("link", {
    rel: "preconnect",
    href: isProduction ? 'https://cdn.meepshop.com' : 'https://cdn.meepstage.com/'
  }), (lcpImage === null || lcpImage === void 0 ? void 0 : lcpImage.scaledSrc) instanceof Object && /*#__PURE__*/React.createElement("link", {
    rel: "preload",
    as: "image",
    imagesrcset: "".concat(lcpImage === null || lcpImage === void 0 ? void 0 : (_lcpImage$scaledSrc = lcpImage.scaledSrc) === null || _lcpImage$scaledSrc === void 0 ? void 0 : _lcpImage$scaledSrc.w480, " 480w, ").concat(lcpImage === null || lcpImage === void 0 ? void 0 : (_lcpImage$scaledSrc2 = lcpImage.scaledSrc) === null || _lcpImage$scaledSrc2 === void 0 ? void 0 : _lcpImage$scaledSrc2.w720, " 720w , ").concat(lcpImage === null || lcpImage === void 0 ? void 0 : (_lcpImage$scaledSrc3 = lcpImage.scaledSrc) === null || _lcpImage$scaledSrc3 === void 0 ? void 0 : _lcpImage$scaledSrc3.w960, " 960w, ").concat(lcpImage === null || lcpImage === void 0 ? void 0 : (_lcpImage$scaledSrc4 = lcpImage.scaledSrc) === null || _lcpImage$scaledSrc4 === void 0 ? void 0 : _lcpImage$scaledSrc4.w1200, " 1200w,").concat(lcpImage === null || lcpImage === void 0 ? void 0 : (_lcpImage$scaledSrc5 = lcpImage.scaledSrc) === null || _lcpImage$scaledSrc5 === void 0 ? void 0 : _lcpImage$scaledSrc5.w1440, " 1440w,").concat(lcpImage === null || lcpImage === void 0 ? void 0 : (_lcpImage$scaledSrc6 = lcpImage.scaledSrc) === null || _lcpImage$scaledSrc6 === void 0 ? void 0 : _lcpImage$scaledSrc6.w1680, " 1680w,").concat(lcpImage === null || lcpImage === void 0 ? void 0 : (_lcpImage$scaledSrc7 = lcpImage.scaledSrc) === null || _lcpImage$scaledSrc7 === void 0 ? void 0 : _lcpImage$scaledSrc7.w1920, " 1920w")
  }), /*#__PURE__*/React.createElement("link", {
    rel: "preconnect",
    href: "https://fonts.googleapis.com"
  }), /*#__PURE__*/React.createElement("link", {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous"
  }), /*#__PURE__*/React.createElement("link", {
    href: "https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@400;500;600;700;900&display=swap",
    rel: "stylesheet"
  }), /*#__PURE__*/React.createElement("meta", {
    key: "keywords",
    name: "keywords",
    content: storeName
  }), /*#__PURE__*/React.createElement("meta", {
    key: "description",
    name: "description",
    content: storeDescription
  }), /*#__PURE__*/React.createElement("meta", {
    key: "og:type",
    property: "og:type",
    content: "website"
  }), /*#__PURE__*/React.createElement("meta", {
    key: "og:url",
    property: "og:url",
    content: "https://".concat(router.domain).concat(router.asPath)
  }), /*#__PURE__*/React.createElement("meta", {
    key: "og:title",
    property: "og:title",
    content: storeName
  }), /*#__PURE__*/React.createElement("meta", {
    key: "og:site_name",
    property: "og:site_name",
    content: storeName
  }), /*#__PURE__*/React.createElement("meta", {
    key: "og:description",
    property: "og:description",
    content: storeDescription
  }), /*#__PURE__*/React.createElement("meta", {
    key: "og:image",
    property: "og:image",
    content: logoImage === null || logoImage === void 0 ? void 0 : (_logoImage$scaledSrc = logoImage.scaledSrc) === null || _logoImage$scaledSrc === void 0 ? void 0 : _logoImage$scaledSrc.w1200
  }), /*#__PURE__*/React.createElement("meta", {
    key: "og:image:width",
    property: "og:image:width",
    content: "400"
  }), /*#__PURE__*/React.createElement("meta", {
    key: "og:image:height",
    property: "og:image:height",
    content: "300"
  }), /*#__PURE__*/React.createElement("meta", {
    key: "og:locale",
    property: "og:locale",
    content: i18n.language
  })), /*#__PURE__*/React.createElement(ErrorBoundary, {
    client: client,
    FallbackComponent: ErrorPageNotFound
  }, /*#__PURE__*/React.createElement(GlobalProvider, {
    lcpImage: lcpImage
  }, /*#__PURE__*/React.createElement(FbProvider, null, /*#__PURE__*/React.createElement(RoleProvider, null, /*#__PURE__*/React.createElement(StoreDesignProvider, {
    pageBackgroundOverride: page === null || page === void 0 ? void 0 : page.background
  }, /*#__PURE__*/React.createElement(AppsProvider, null, /*#__PURE__*/React.createElement(SensorProvider, null, /*#__PURE__*/React.createElement(CurrencyProvider, null, /*#__PURE__*/React.createElement(AdTrackProvider, {
    pageId: // 商品頁使用商品 ID 作為唯一值，因為現行系統設計商品頁若是套用預設版型，則會共用相同 pageId，故無法當作唯一值做使用
    ['/products/[pId]', '/product/[pId]'].includes(router.pathname) ? router.query.pId : page === null || page === void 0 ? void 0 : page.id,
    snapshotId: page === null || page === void 0 ? void 0 : page.snapshotId
  }, /*#__PURE__*/React.createElement(FormDataProvider, null, /*#__PURE__*/React.createElement(Component, _extends({}, pageProps, {
    product: product,
    loading: loading,
    page: page,
    client: client,
    globalCarousel: globalCarousel,
    globalPopup: globalPopup,
    url: {
      asPath: router.asPath,
      query: router.query
    }
  })), /*#__PURE__*/React.createElement(ActionButton, {
    backToTopButtonEnabled: backToTopButtonEnabled,
    goToButton: (page === null || page === void 0 ? void 0 : page.goToButton) || null
  }))))))))))));
};

export default appWithTranslation(appWithApollo(App));